// Changing a plan for sales from now on (membershipTypes.update): the form, what changed, and the
// words. Memberships already sold keep the terms they were sold with; a new price goes through its
// price book item, kept in the price history.

import type { CancelRefund, Plan } from './model.ts';

/** membershipTypes.list's row, with what an edit needs. */
export type EditablePlan = Plan & { visitJobTypeId?: string | null; rowVersion?: number };

export interface PlanForm {
  name: string;
  price: string;
  /** Months a term (one imported bare has none yet). */
  term: string;
  visits: string;
  jobType: string;
  discount: string;
  active: boolean;
  /** At a cancel (BL-056): applies to every member of the plan when they cancel. */
  cancelRefund: CancelRefund;
}

export const planForm = (p: EditablePlan): PlanForm => ({
  name: p.name,
  price: p.priceCents === null ? '' : (p.priceCents / 100).toFixed(2),
  term: p.termMonths === null ? '' : String(p.termMonths),
  visits: p.visitsPerTerm === null ? '' : String(p.visitsPerTerm),
  jobType: p.visitJobTypeId ?? '',
  discount: String(Number(p.discountPct)),
  active: p.active,
  cancelRefund: p.cancelRefund ?? 'optional',
});

const cents = (s: string): number | null => {
  const t = s.replace(/[$,\s]/g, '');
  return /^\d+(\.\d{1,2})?$/.test(t) ? Math.round(Number(t) * 100) : null;
};

export function planProblems(f: PlanForm): string[] {
  const out: string[] = [];
  if (!f.name.trim()) out.push('Give the plan a name.');
  if (cents(f.price) === null) out.push('A price like 199.00.');
  const t = Number(f.term);
  if (!/^\d+$/.test(f.term.trim()) || t < 1 || t > 60) out.push('A term in months: a whole number, 1 to 60.');
  const v = Number(f.visits);
  if (!/^\d+$/.test(f.visits.trim()) || v > 52) out.push('Visits a term: a whole number, 0 to 52.');
  else if (v > 0 && !f.jobType) out.push('Pick the job type the included visits are.');
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(f.discount.trim()) || Number(f.discount) > 100)
    out.push('A discount like 10 or 12.5 (percent).');
  return out;
}

/** Only what changed, with the version it was read at; null when nothing did. */
export function planUpdate(p: EditablePlan, f: PlanForm): Record<string, unknown> | null {
  const was = planForm(p);
  const out: Record<string, unknown> = {};
  if (f.name.trim() !== was.name) out.name = f.name.trim();
  if (cents(f.price) !== p.priceCents) out.priceCents = cents(f.price);
  if (Number(f.term) !== p.termMonths) out.termMonths = Number(f.term);
  if (Number(f.visits) !== p.visitsPerTerm) out.visitsPerTerm = Number(f.visits);
  const jt = Number(f.visits) > 0 ? f.jobType || null : null;
  if (jt !== (p.visitJobTypeId ?? null)) out.visitJobTypeId = jt;
  if (Number(f.discount) !== Number(p.discountPct)) out.discountPct = f.discount.trim();
  if (f.active !== p.active) out.active = f.active;
  if (f.cancelRefund !== was.cancelRefund) out.cancelRefund = f.cancelRefund;
  if (!Object.keys(out).length) return null;
  return { id: p.id, ...(p.rowVersion ? { rowVersion: p.rowVersion } : {}), ...out };
}

/** "Comfort Club is changed for sales from now on; 12 members keep the terms they bought." */
export function planSavedText(name: string, members: number): string {
  return members
    ? `${name} is changed for sales from now on; ${members} ${members === 1 ? 'member keeps' : 'members keep'} the terms they bought.`
    : `${name} is changed.`;
}
